import { ReactNode, useCallback, useEffect, useState } from 'react';
import { addClGiftCard } from 'actions/client';
import { creditCardSchema } from 'constant';
import { FormikValues, useFormik } from 'formik';
import { useGetApptConfirmationSettingsQuery } from 'services/bookingApi';
import {
  useAddedPaymentMethodMutation,
  useGetPaymentMethodsQuery
} from 'services/paymentMethodsApi';

import { Box, Flex, useTheme, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearSelectedCards } from 'store/reducers/bookingAppointmentSlice';
import { setError } from 'store/reducers/cartSlice';
import { setError as setClientError } from 'store/reducers/clientSlice';

import { formatCurrency, removeSpaces } from 'utils';

import {
  CCardBase,
  CCardInterface,
  DepositOptionsInterface,
  ScreenTypeEnum
} from 'types';

import { AddGiftCardForm, GiftCardPaymentList, PaymentList } from 'components';
import { AddCardForm } from 'components/AddCardForm';
import { AddCardBox } from 'components/PaymentList';
import { Button, GiftCardErrorModal, PaymentLoader } from 'ui';

const initialValues: FormikValues = {
  card_number: '',
  card_cvv2: '',
  exp_month: '',
  exp_year: '',
  owner: '',
  addr_ln1: '',
  city: '',
  state: '',
  zip: ''
};

interface CardsPaymentsListProps {
  depositOptions: DepositOptionsInterface | null;
  isButtonDisabled: boolean;
  onBack: () => void;
  children: ReactNode;
  onPaymentConfirmation: (id?: number | null) => void;
}

const CardsPaymentsList = ({
  isButtonDisabled = false,
  onBack,
  children,
  onPaymentConfirmation,
  depositOptions
}: CardsPaymentsListProps) => {
  const {
    colors: {
      palette: {
        components: { paymentList: styles }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { giftCards, errorMsg: clientError } = useAppSelector(
    (state) => state.client
  );
  const { errorMsg, isLoading: isCartLoading } = useAppSelector(
    (state) => state.cart
  );
  const { moveApptId } = useAppSelector((state) => state.bookingAppointment);

  const { data: clientAccounts = [] } = useGetPaymentMethodsQuery('', {
    refetchOnMountOrArgChange: true
  });
  const [addCreditCard, { error: addErrorMsg = '', reset: clearErrors }] =
    useAddedPaymentMethodMutation();

  const [mode, setMode] = useState<ScreenTypeEnum>(ScreenTypeEnum.cardList);
  const [selectedCCardId, selectCCardId] = useState<number | null>(null);
  const [isCardProcessing, setIsCardProcessing] = useState<boolean>(false);

  const { data: bookingConfirmation } = useGetApptConfirmationSettingsQuery('');

  const isCConFileRequired = bookingConfirmation?.cc_on_file_require;
  // cc_allow_to_skip = 1 only valid when cc_on_file_require = 1
  const isCCAllowToSkip = bookingConfirmation?.cc_allow_to_skip;
  const isCardsExist = clientAccounts && clientAccounts?.length > 0;
  const currentCard = clientAccounts?.find(
    (card) => card.id === selectedCCardId
  );

  const onScreenTypeChange = useCallback(
    (type: ScreenTypeEnum) => {
      setMode(type);
    },
    [setMode]
  );

  const handleClearError = () => {
    dispatch(setError(''));
  };

  useEffect(() => {
    if (!isCartLoading) {
      if (
        (clientAccounts === null ||
          (clientAccounts && clientAccounts?.length === 0)) &&
        !(Boolean(depositOptions?.req_deposit) && Boolean(giftCards?.length))
      ) {
        onScreenTypeChange(ScreenTypeEnum.newCardForm);
      } else if (clientAccounts && clientAccounts?.length > 0) {
        onScreenTypeChange(ScreenTypeEnum.cardList);
      }
    }
  }, [
    clientAccounts,
    isCartLoading,
    onScreenTypeChange,
    depositOptions?.req_deposit,
    giftCards
  ]);

  useEffect(() => {
    dispatch(clearSelectedCards());
  }, [dispatch]);

  const clearPaymentLoderState = () => {
    selectCCardId(null);
    setIsCardProcessing(false);
  };

  const formikData = useFormik({
    initialValues,
    validationSchema: creditCardSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (formData: FormikValues) => {
      if (userInfo) {
        const cardData: CCardInterface = {
          ...(formData as CCardBase),
          card_number: removeSpaces(formData.card_number),
          allow_billing: 2,
          exp_month: +formData.exp_month,
          exp_year: +formData.exp_year,
          clid: userInfo.clid,
          token: userInfo.token
        };
        const card = await addCreditCard(cardData).unwrap();

        if (card?.id) {
          onScreenTypeChange(ScreenTypeEnum.cardList);
          selectCCardId(card?.id);
        }
      }
    }
  });

  const giftCardFormData = useFormik({
    initialValues: { cardNumber: '' },
    onSubmit: async ({ cardNumber }: FormikValues) => {
      dispatch(addClGiftCard(cardNumber)).then((res) => {
        if (res) {
          onScreenTypeChange(ScreenTypeEnum.cardList);
        }
      });
    }
  });

  const onBook = async () => {
    setIsCardProcessing(true);

    try {
      await onPaymentConfirmation(currentCard?.id);
    } finally {
      clearPaymentLoderState();
    }
  };

  const renderMode = () => {
    switch (mode) {
      case ScreenTypeEnum.newCardForm:
        return (
          <AddCardForm
            formikData={formikData}
            errorMsg={addErrorMsg as string}
            clearErrors={clearErrors}
          />
        );
      case ScreenTypeEnum.newGiftCardForm:
        return (
          <AddGiftCardForm
            formData={giftCardFormData}
            bg={styles.cardBg}
            color={styles.label}
            px='50px'
          />
        );
      case ScreenTypeEnum.cardList:
        return (
          <PaymentList
            onTypeHandler={onScreenTypeChange}
            onClearError={handleClearError}
            onCardSelect={selectCCardId}
            errorMsg={errorMsg}
            isPaymentLoading={isCartLoading}
            initialId={selectedCCardId}
            handlePayment={onBook}
          />
        );
      default:
        return null;
    }
  };

  const defaultBackBtn = (
    <Button variant='outline' w='150px' onClick={onBack}>
      Back
    </Button>
  );

  const cancelFormBtn = (
    <Button
      variant='outline'
      w='150px'
      onClick={() => {
        onScreenTypeChange(ScreenTypeEnum.cardList);
        formikData.resetForm();
        giftCardFormData.resetForm();
      }}
    >
      Cancel
    </Button>
  );

  const renderBackBtn = () => {
    switch (mode) {
      case ScreenTypeEnum.cardList:
        return defaultBackBtn;
      case ScreenTypeEnum.newGiftCardForm:
        return cancelFormBtn;
      case ScreenTypeEnum.newCardForm:
        return isCardsExist || Boolean(giftCards.length)
          ? cancelFormBtn
          : defaultBackBtn;
      default:
        return defaultBackBtn;
    }
  };

  const clearClientError = () => dispatch(setClientError(''));

  const renderNextButton = () => {
    switch (mode) {
      case ScreenTypeEnum.newCardForm:
        const { dirty, isValid } = formikData;
        const isAllowToSkipStep =
          clientAccounts?.length === 0 && isCCAllowToSkip && isCConFileRequired;
        const isSubmitDisabled =
          (!dirty || !isValid || isCartLoading) && !isAllowToSkipStep;

        return (
          <Button
            w='150px'
            isDisabled={isSubmitDisabled}
            onClick={
              isAllowToSkipStep && !isValid
                ? onBook
                : () => formikData.handleSubmit()
            }
          >
            {isAllowToSkipStep && !isValid ? 'Skip' : 'Next'}
          </Button>
        );
      case ScreenTypeEnum.newGiftCardForm:
        return (
          <Button
            w='150px'
            isDisabled={!giftCardFormData.values.cardNumber}
            onClick={() => giftCardFormData.handleSubmit()}
          >
            Save
          </Button>
        );

      case ScreenTypeEnum.cardList:
        const additionalRequirement =
          Boolean(depositOptions?.req_deposit) ||
          Boolean(depositOptions?.req_guarantee) ||
          isButtonDisabled;

        return (
          <Button
            w='150px'
            isDisabled={currentCard ? false : additionalRequirement}
            onClick={onBook}
          >
            {moveApptId ? 'Move Now' : 'Book Now'}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box
        borderTop='1px solid'
        borderBottom='1px solid'
        borderColor={styles.divider}
        py='20px'
      >
        {renderMode()}

        {Boolean(depositOptions?.req_deposit) &&
          mode === ScreenTypeEnum.cardList && (
            <VStack gap='5px'>
              <GiftCardPaymentList
                pt='20px'
                mt='20px'
                borderTop='1px solid'
                borderColor={styles.divider}
              />

              <AddCardBox
                label={isCardsExist ? 'Add Another Gift Card' : 'Add Gift Card'}
                onSelectCard={() => {
                  onScreenTypeChange(ScreenTypeEnum.newGiftCardForm);
                  formikData.resetForm();
                }}
              />
            </VStack>
          )}
      </Box>
      {children}
      <Flex
        direction={{ base: 'column-reverse', md: 'row' }}
        mt={{ base: '30px', md: '50px' }}
        justifyContent='center'
        alignItems='center'
        gap={{ base: '20px', md: '30px' }}
        w='100%'
      >
        {renderBackBtn()}

        {renderNextButton()}
      </Flex>
      {isCardProcessing && (
        <PaymentLoader
          color={styles.overlayTextColor}
          bg={styles.overlayBg}
          text={`Processing Payment of ${formatCurrency(
            depositOptions?.total_appt_amount || 0
          )}`}
        />
      )}
      {clientError && (
        <GiftCardErrorModal
          bg='blackOpacity.16'
          serial={giftCardFormData.values.cardNumber}
          onBack={clearClientError}
        />
      )}
    </>
  );
};

export default CardsPaymentsList;
